<script setup>
import { computed, ref } from 'vue'
import AspLoadingIcon from './AspLoadingIcon.vue'

const props = defineProps({
    loading: {
        type: Boolean,
        default: false,
    },
    simulateLoading: {
        type: Boolean,
        default: false,
    },
    simulateDelay: {
        type: Number,
        default: null, // null means indefinite, number means auto-clear after delay
    },
    color: {
        type: String,
        default: 'primary',
    },
    variant: {
        type: String,
        default: 'flat',
    },
    size: {
        type: String,
        default: 'default',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    loaderSize: {
        type: [String, Number],
        default: 20,
    },
    hoverColor: {
        type: String,
        default: null, // If not provided, will check colorHoverMap, then auto-darken
    },
    hoverAnimation: {
        type: Boolean,
        default: true, // Enabled by default
    },
    hoverLift: {
        type: Boolean,
        default: false, // Disabled by default
    },
    hoverBrightness: {
        type: Number,
        default: 0.85, // Darken to 85% brightness by default
    },
    // Pass through all other VBtn props
})

const emit = defineEmits(['click'])

const internalLoading = ref(false)

// Predefined color hover map
const colorHoverMap = {
    'sky-blue': '#3587C2',
    error: '#ED151B',
    white: '#F5F2FF',
    // Add more color mappings here
    // 'primary': '#1976D2',
    // 'secondary': '#424242',
    // 'success': '#4CAF50',
}

// Compute effective loading state (external loading prop or internal simulated loading)
const isLoading = computed(() => props.loading || internalLoading.value)

// Compute button style based on loading state
const buttonClass = computed(() => {
    const classes = []
    if (isLoading.value) classes.push('ucs-btn-loading')
    // Don't add hover classes when loading
    if (!isLoading.value && props.hoverAnimation)
        classes.push('ucs-btn-hover-enabled')
    if (!isLoading.value && props.hoverLift) classes.push('ucs-btn-hover-lift')
    return classes.join(' ')
})

// Compute hover color style
const buttonStyle = computed(() => {
    const styles = {}

    // Priority: 1. Explicit hoverColor prop, 2. colorHoverMap lookup, 3. Auto-darken
    const hasDedicatedHoverColor =
        props.hoverColor || (props.color && colorHoverMap[props.color])

    if (props.hoverColor) {
        styles['--hover-color'] = props.hoverColor
    } else if (props.color && colorHoverMap[props.color]) {
        styles['--hover-color'] = colorHoverMap[props.color]
    }

    // Only apply hover-brightness if no dedicated hover color is defined
    if (!hasDedicatedHoverColor) {
        styles['--hover-brightness'] = props.hoverBrightness
    }

    return styles
})

const handleClick = async (event) => {
    if (isLoading.value || props.disabled) {
        return
    }

    // If simulateLoading is true, show loading state
    if (props.simulateLoading) {
        internalLoading.value = true
        emit('click', event)

        // If simulateDelay is provided, auto-clear after delay
        // Otherwise, keep loading indefinitely (for one-time actions)
        if (props.simulateDelay !== null && props.simulateDelay !== undefined) {
            setTimeout(() => {
                internalLoading.value = false
            }, props.simulateDelay)
        }
        // If no delay provided, loading stays true permanently (button won't be clickable again)
    } else {
        emit('click', event)
    }
}
</script>

<template>
    <VBtn
        :class="buttonClass"
        :style="buttonStyle"
        :color="color"
        :variant="variant"
        :size="size"
        :disabled="disabled"
        :loading="isLoading"
        :ripple="false"
        v-bind="$attrs"
        @click="handleClick"
    >
        <slot />

        <template v-if="isLoading" #loader>
            <AspLoadingIcon :size="loaderSize" />
        </template>
    </VBtn>
</template>

<style lang="scss">
/* Transitions for all buttons */
:deep(.v-btn) {
    transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
        border 0.2s ease-in-out, transform 0.2s ease-in-out,
        box-shadow 0.2s ease-in-out !important;
}

:deep(.v-btn__content) {
    transition: opacity 0.15s ease-in-out !important;
}
</style>

<style lang="scss">
/* Global styles for hover and loading - not scoped */

/* Loading state styles */
.v-btn.ucs-btn-loading {
    background-color: white !important;
    color: white !important;
    border: 1px solid #bdbdbd !important;
    transition: none !important;
}

.v-btn.ucs-btn-loading .v-btn__overlay {
    opacity: 0 !important;
}

.v-btn.ucs-btn-loading .v-btn__content {
    opacity: 0 !important;
    transition: none !important;
}

.v-btn.ucs-btn-loading .v-btn__loader {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    z-index: 2 !important;
}

/* Hover styles - Manual color override */
.v-btn.ucs-btn-hover-enabled[style*='--hover-color']:not(.v-btn--disabled):not(
        .ucs-btn-loading
    ):hover {
    background-color: var(--hover-color) !important;
}

.v-btn.ucs-btn-hover-enabled[style*='--hover-color']:not(.v-btn--disabled):not(
        .ucs-btn-loading
    ):hover
    .v-btn__overlay {
    opacity: 0 !important;
}

/* Hover styles - Auto-darken with overlay */
.v-btn.ucs-btn-hover-enabled:not(.v-btn--disabled):not(
        .ucs-btn-loading
    ):hover::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, calc(1 - var(--hover-brightness)));
    border-radius: inherit;
    pointer-events: none;
    z-index: 0;
}

/* Ensure content stays above overlay */
.v-btn.ucs-btn-hover-enabled .v-btn__content {
    position: relative;
    z-index: 1;
}

/* Lift effect */
.v-btn.ucs-btn-hover-lift:not(.v-btn--disabled):not(.ucs-btn-loading):hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-btn.ucs-btn-hover-lift:not(.v-btn--disabled):not(.ucs-btn-loading):active {
    transform: translateY(0) !important;
}
</style>
